import { useState } from "react";
import { ICellRendererParams } from "ag-grid-community";
import { FaPercentage } from "react-icons/fa";
import { Loader2, Plus } from "lucide-react";
import { Button } from "components/common/buttons/Button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "components/common/dropdown/DropDownV2";
import { CellRendererBase } from "../CellRendererBase";
import { BudgetFringe, BudgetFringeUnit, BudgetItem, BudgetTotal } from "../types";
import { FringeLineBlock } from "../fringes.utils";

/** The budget's fringe list. Until it loads, the menu offers only the line's own fringes. */
export type FringeListState =
  | { status: "loaded"; fringes: BudgetFringe[] }
  | { status: "loading" }
  | { status: "failed"; onRetry: () => void; isRetrying: boolean };

export interface FringesCellParams {
  /** Every fringe in the budget. */
  fringeList: FringeListState;
  /** Why the line can't change at all, for example a viewer or a locked estimate. */
  lockedReason: string;
  getBlock: (item: BudgetItem) => FringeLineBlock | null;
  blockReason: (block: FringeLineBlock) => string;
  onSave: (item: BudgetItem, fringes: BudgetFringe[]) => void;
}

const isBudgetItem = (data: unknown): data is BudgetItem =>
  typeof data === "object" && data !== null && "budget_item_id" in data;

const rateLabel = (fringe: BudgetFringe) =>
  fringe.unit === BudgetFringeUnit.PERCENT
    ? `${Number(fringe.rate)}%`
    : `${Number(fringe.rate)} per ${fringe.unit.replace(/s$/, "")}`;

const FringeNames = ({ fringes }: { fringes: BudgetFringe[] }) =>
  fringes.length ? (
    <span className="flex min-w-0 items-center gap-1">
      <span className="truncate">{fringes.map((fringe) => fringe.name).join(", ")}</span>
      <FaPercentage className="shrink-0" aria-hidden />
    </span>
  ) : null;

export const FringesCell = (
  params: ICellRendererParams<BudgetItem | BudgetTotal> & FringesCellParams
) => {
  const { data, fringeList, lockedReason, getBlock, blockReason, onSave } = params;
  const budgetFringes = fringeList.status === "loaded" ? fringeList.fringes : [];
  const item = isBudgetItem(data) ? data : null;
  const lineFringes = item?.fringes ?? [];
  const block = item ? getBlock(item) : null;
  // A line without an account number can still drop fringes it already has.
  const canAdd = !lockedReason && block === null;
  const canRemove = !lockedReason && (block === null || block === "noAccountNumber");
  const editable = Boolean(item) && (canAdd || (canRemove && lineFringes.length > 0));

  const [open, setOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  if (!item) return null;

  if (!editable) {
    return (
      <CellRendererBase params={params} nonEditableReason={lockedReason || blockReason(block!)}>
        <FringeNames fringes={lineFringes} />
      </CellRendererBase>
    );
  }

  const options = [
    ...budgetFringes,
    // Keep fringes the list hasn't loaded yet, so opening the menu never drops them.
    ...lineFringes.filter(
      (fringe) => !budgetFringes.some((entry) => entry.budget_fringe_id === fringe.budget_fringe_id)
    ),
  ];

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setSelectedIds(lineFringes.map((fringe) => fringe.budget_fringe_id));
    } else {
      const current = lineFringes.map((fringe) => fringe.budget_fringe_id);
      const changed =
        selectedIds.length !== current.length || selectedIds.some((id) => !current.includes(id));
      if (changed) {
        onSave(
          item,
          options.filter((fringe) => selectedIds.includes(fringe.budget_fringe_id))
        );
      }
    }
    setOpen(next);
  };

  const toggle = (fringeId: number) =>
    setSelectedIds((current) =>
      current.includes(fringeId) ? current.filter((id) => id !== fringeId) : [...current, fringeId]
    );

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Fringes on ${item.name}`}
          className="group flex h-full w-full min-w-0 items-center px-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/50"
        >
          {lineFringes.length ? (
            <FringeNames fringes={lineFringes} />
          ) : (
            <span className="flex items-center gap-1 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
              <Plus className="h-3.5 w-3.5" aria-hidden />
              Add
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>Fringes on this line</DropdownMenuLabel>
        {fringeList.status === "loaded" && options.length === 0 ? (
          <p className="px-2 py-1.5 text-sm text-muted-foreground">
            No fringes yet. Add one on the Fringes page.
          </p>
        ) : (
          options.map((fringe) => {
            const checked = selectedIds.includes(fringe.budget_fringe_id);
            return (
              <DropdownMenuCheckboxItem
                key={fringe.budget_fringe_id}
                checked={checked}
                disabled={checked ? !canRemove : !canAdd}
                // Keep the menu open so several fringes can change in one save.
                onSelect={(event) => event.preventDefault()}
                onCheckedChange={() => toggle(fringe.budget_fringe_id)}
              >
                <span className="min-w-0 flex-1 truncate">{fringe.name}</span>
                <span className="shrink-0 text-xs text-muted-foreground">{rateLabel(fringe)}</span>
              </DropdownMenuCheckboxItem>
            );
          })
        )}
        {fringeList.status === "loading" ? (
          <div
            role="status"
            aria-label="Loading fringes"
            className="flex items-center gap-2 px-2 py-1.5 text-sm text-muted-foreground"
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            Loading fringes…
          </div>
        ) : null}
        {fringeList.status === "failed" ? (
          <div role="alert" className="flex flex-col items-start gap-2 px-2 py-1.5">
            <p className="text-sm text-muted-foreground">Couldn&apos;t load fringes</p>
            <DropdownMenuItem
              asChild
              // Keep the menu open so the list appears here once it loads.
              onSelect={(event) => {
                event.preventDefault();
                fringeList.onRetry();
              }}
            >
              <Button
                type="button"
                variant="badge"
                color="gray"
                size="sm"
                isLoading={fringeList.isRetrying}
              >
                Try again
              </Button>
            </DropdownMenuItem>
          </div>
        ) : null}
        {!canAdd && block ? (
          <p className="px-2 py-1.5 text-xs text-muted-foreground">{blockReason(block)}</p>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
